import { override } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import registerTagAttributes from './utils/tagAttributes';

// The chunked TagSelectionModal/ToggleButton pair below is only ever
// reachable through flarum.reg's own lazy-loading machinery (see the
// docblock further down), never a static import - both onLoad()'s module
// param and get()'s return value are typed `any` by core's own
// ExportRegistry.d.ts (there's no way it could know the shape of a module
// it hasn't loaded yet), same as every other flarum.reg.get()/.onLoad()
// lookup in this codebase. The bare global `flarum` used below (no
// `window.`) is core's own `declare const flarum: FlarumObject` (see
// vendor/flarum/core/js/dist-typings/@types/global.d.ts) - real at
// runtime the same way `window.flarum` is (core's boot assigns it there),
// just referenced directly here rather than through `window.` the way
// every other file in this codebase happens to.

// Replaces flarum-tags' filterable click-through tag list with a plain
// <select> dropdown. On this forum tags are really a single required
// category per discussion, and picking a child automatically pulls in its
// parent (see selectCategory() below, a full-ancestor-chain version of
// TagSelectionModal's own addTag()) - so one dropdown pick is all a
// discussion ever needs. Hence flarum-tags.min/max_primary_tags both 1, and
// min/max_secondary_tags 1 and 2: a third-level category's chain (Rédactions
// > Livres > Livres de référence) holds two non-primary tags, which a max of
// 1 refused, admin or not (reported 2026-10-11). Used by both places
// flarum-tags opens this modal: the composer (addTagComposer.js) and the
// "edit tags" moderation control (addTagControl.js), since both go through
// TagDiscussionModal -> TagSelectionModal.
//
// TagSelectionModal (and its ToggleButton dependency) are lazy-chunked by
// flarum-tags - only downloaded once its tag-picker modal is first opened -
// so `import ... from 'ext:flarum/tags/...'` can't be used here: that
// resolves via an immediate flarum.reg.get() at module-evaluation time,
// which runs long before the chunk has loaded and silently returns
// undefined (this previously crashed the whole champis-species initializer
// with "Cannot read properties of undefined (reading 'prototype')", since
// module-eval happens before any app.initializers callback runs).
// flarum.reg.onLoad() defers the callback until the chunk has genuinely
// registered the module - the same mechanism extend()/override() use
// internally for their string-target form.
export default function categorySelect() {
  registerTagAttributes();
  flarum.reg.onLoad('flarum-tags', 'common/components/TagSelectionModal', TagSelectionModal => {
    // Sets the selection to the chosen tag plus its *entire* ancestor chain
    // (not just its immediate parent, unlike TagSelectionModal's own
    // addTag) so a min-primary-tags requirement is met even for a tag
    // nested more than one level deep (e.g. under "Liens").
    TagSelectionModal.prototype.selectCategory = function (id) {
      const tag = id ? app.store.getById('tags', id) || null : null;
      if (!tag) {
        this.selected = [];
        return;
      }
      const chain = [];
      let t = tag;
      while (t) {
        chain.unshift(t);
        t = t.parent() || null;
      }
      this.selected = chain;
    };
    override(TagSelectionModal.prototype, 'content', function () {
      if (this.loading || !this.tags) {
        return m(LoadingIndicator, null);
      }
      const groups = buildGroups(this.tags);
      const selected = deepestSelected(this.selected);
      const primaryCount = this.primaryCount();
      const secondaryCount = this.secondaryCount();
      const ready = this.meetsRequirements(primaryCount, secondaryCount);
      return m("div", {
        className: "Modal-body"
      }, m("div", {
        className: "ChampisCategorySelect-field"
      }, m("label", {
        className: "ChampisCategorySelect-label"
      }, app.translator.trans('champis-forum.forum.category_select.label')), m("select", {
        className: "FormControl",
        value: selected ? String(selected.id()) : '',
        onchange: e => {
          this.selectCategory(e.target.value);
          this.onready();
        }
      }, m("option", {
        value: "",
        disabled: true
      }, app.translator.trans('champis-forum.forum.category_select.placeholder')), groups.map(group => m("optgroup", {
        key: group.root.id(),
        label: group.root.name()
      }, group.options.map(_ref => {
        let {
          tag,
          depth,
          selectable
        } = _ref;
        return m("option", {
          key: tag.id(),
          value: tag.id(),
          disabled: !selectable
        }, '— '.repeat(Math.max(depth - 1, 0)), tag.name());
      }))))), !ready && m("p", {
        className: "ChampisCategorySelect-hint"
      }, app.translator.trans('champis-forum.forum.category_select.required_hint')), this.attrs.limits.allowBypassing && (() => {
        // Resolved fresh here rather than imported at module scope -
        // it's bundled in the same lazy chunk as TagSelectionModal,
        // so by the time this renders it's guaranteed to be
        // registered.
        const ToggleButton = flarum.reg.get('flarum-tags', 'forum/components/ToggleButton');
        return m("div", {
          className: "ChampisCategorySelect-controls"
        }, m(ToggleButton, {
          className: "Button",
          onclick: () => this.bypassReqs = !this.bypassReqs,
          isToggled: this.bypassReqs
        }, app.translator.trans('flarum-tags.lib.tag_selection_modal.bypass_requirements')));
      })(), m("div", {
        className: "ChampisCategorySelect-submit App-primaryControl"
      }, m(Button, {
        type: "submit",
        className: "Button Button--primary",
        disabled: !ready,
        icon: "fas fa-check"
      }, app.translator.trans('champis-forum.forum.category_select.submit_button'))));
    });
  });
}
function ancestorDepth(tag) {
  let depth = 0;
  let t = tag;
  while (t.parent()) {
    depth++;
    t = t.parent();
  }
  return depth;
}
function deepestSelected(selected) {
  if (!selected.length) return null;
  return selected.reduce((deepest, tag) => ancestorDepth(tag) >= ancestorDepth(deepest) ? tag : deepest, selected[0]);
}
// Groups the (already permission-filtered) selectable tags by their
// top-level ancestor, for use as <optgroup>s - nested tags below that get
// an indentation prefix rather than their own group, since our tag
// hierarchy on this forum occasionally goes 3 levels deep (e.g. "Liens" ->
// "Ascomycètes") but a <select> only has one level of grouping. A root tag
// with no children of its own (e.g. "General") becomes a single-option
// group selecting itself, so it stays reachable.
//
// Sorted by champisOrder (phpBB's original left_id order, preserved by
// migration/02_tags.php) rather than left in whatever order TagSelectionModal's
// own sortTags() produced - that ranks secondary tags by discussion count,
// fine for a flat tag cloud but scrambles a forum hierarchy that's meant
// to read top-to-bottom in its original order.
//
// An intermediate tag (has children of its own, e.g. "Liens") is still
// shown - dropping it would leave its children as unlabelled floating
// entries - but marked non-`selectable`, since it exists purely to
// organize those children and isn't a meaningful category by itself. The
// caller renders that as a disabled <option>: still visible (and still
// displayable as a discussion's *current* category, for the rare
// pre-existing discussion tagged with one directly), just not pickable
// going forward.
function buildGroups(tags) {
  const sorted = [...tags].sort((a, b) => a.champisOrder() - b.champisOrder());
  const hasChildren = tag => sorted.some(t => t.parent() && String(t.parent().id()) === String(tag.id()));
  const roots = sorted.filter(tag => !tag.parent());
  const groups = roots.map(root => ({
    root,
    options: []
  }));
  const groupByRootId = new Map(groups.map(group => [String(group.root.id()), group]));
  sorted.forEach(tag => {
    if (!tag.parent()) return;
    let top = tag;
    while (top.parent()) top = top.parent();
    const group = groupByRootId.get(String(top.id()));
    if (group) group.options.push({
      tag,
      depth: ancestorDepth(tag),
      selectable: !hasChildren(tag)
    });
  });
  groups.forEach(group => {
    if (!group.options.length) group.options.push({
      tag: group.root,
      depth: 0,
      selectable: true
    });
  });
  return groups.filter(group => group.options.length);
}